import axios from "axios";
import storageService from "@/services/storage.service";
import { createWebHistory, createRouter } from "vue-router";
import routes from "./routes";
import { startLoader, stopLoader } from "@/services/loader";
import {
  progress,
  isLockedPath,
  refreshApplicationProgress,
} from "@/services/applicantProgress";

const router = createRouter({
  // The base path comes from the history factory in Vue Router 4
  // (the old `mode` / `base` options are ignored).
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

const adminPrefixes = [
  "/admin_dashboard",
  "/staff_users",
  "/single_applicant",
  "/applicants",
  "/admin_notifications",
  "/submit",
  "/admin_in_review",
  "/admin_verified",
  "/archived_staff_members",
  "/single_staff",
  "/edit_staff",
];

const staffPrefixes = [
  "/application_list",
  "/staff_view_log",
  "/staff_dashboard",
  "/approved_application",
  "/rejected_application",
  "/incomplete_application",
  "/pdf_data",
  "/staff_notification",
  "/my_profile",
  "/view_application",
  "/review_application",
];

// Pages only an applicant opens (their own submitted application).
const applicantOnlyPrefixes = ["/my_application"];

// Router Navigation Guard with Role-Based Access Control.
// NOTE: this only decides which pages to show. It is a UX convenience; real
// authorization is enforced by the backend on every API call.
let roleVerifiedToken = null;

const verifyServerRole = async (accessToken) => {
  if (roleVerifiedToken === accessToken) return;
  try {
    const res = await axios.get("/api/getAuthenticatedUser/");
    const serverRole = res && res.data && res.data.role;
    if (serverRole) {
      storageService.setItem("userType", serverRole);
    }
    roleVerifiedToken = accessToken;
  } catch (err) {
    if (err && err.response && err.response.status === 403) {
      storageService.removeItem("accessToken");
      storageService.removeItem("refreshToken");
      storageService.removeItem("userType");
    }
  }
};

// Page loading: show the loader only when a navigation (lazy chunk + role check)
// takes noticeably long.
let navigationLoader = null;
const endNavigationLoader = () => {
  stopLoader(navigationLoader);
  navigationLoader = null;
};
router.afterEach(endNavigationLoader);
router.onError(endNavigationLoader);

router.beforeEach(async (to, from, next) => {
  stopLoader(navigationLoader);
  navigationLoader = startLoader({ delay: 400, message: "Loading..." });
  const publicPages = [
    "/login",
    "/email",
    "/register",
    "/forgot-password",
    "/reset-password",
    "/set-password",
    "/staff_login",
    "/auth/login-1",
  ];
  const isPublicPage = publicPages.some(
    (p) => to.path === p || to.path.startsWith(p),
  );
  if (
    !isPublicPage &&
    to.path !== "/logout" &&
    storageService.getItem("accessToken")
  ) {
    await verifyServerRole(storageService.getItem("accessToken"));
  }
  const accessToken = storageService.getItem("accessToken");
  const userRole = storageService.getItem("userType");

  // 1. Unauthenticated users trying to access protected routes
  if (!isPublicPage && !accessToken) {
    if (
      to.path.startsWith("/admin") ||
      to.path.startsWith("/staff") ||
      staffPrefixes.some((p) => to.path.startsWith(p))
    ) {
      return next("/staff_login");
    }
    return next("/login");
  }

  // 2. Already authenticated users trying to access login pages
  if (isPublicPage && accessToken && to.path !== "/logout") {
    if (userRole === "admin" || userRole === "super_user") {
      return next("/admin_dashboard");
    } else if (userRole === "staff" || userRole === "verification officer") {
      return next("/staff_dashboard");
    } else {
      return next("/");
    }
  }

  if (accessToken && userRole === "applicant" && isLockedPath(to.path)) {
    if (!progress.submitted) {
      const answer = await refreshApplicationProgress();
      if (answer === false || (answer === null && progress.known)) {
        return next("/membership_application");
      }
    }
  }

  // 3. Role enforcement on protected routes
  if (accessToken && userRole) {
    const isAdminRoute = adminPrefixes.some((p) => to.path.startsWith(p));
    const isStaffRoute = staffPrefixes.some((p) => to.path.startsWith(p));

    // Staff and admins have their own view of an application
    if (
      userRole !== "applicant" &&
      applicantOnlyPrefixes.some((p) => to.path.startsWith(p))
    ) {
      return next(
        userRole === "admin" || userRole === "super_user"
          ? "/admin_dashboard"
          : "/staff_dashboard",
      );
    }

    // Applicants cannot access staff or admin routes
    if (userRole === "applicant" && (isAdminRoute || isStaffRoute)) {
      return next("/");
    }

    // Staff/Verification officers cannot access admin routes
    if (
      (userRole === "staff" || userRole === "verification officer") &&
      isAdminRoute
    ) {
      return next("/staff_dashboard");
    }
  }

  return next();
});

export default router;